/**
 * SPA entry point — wires the QueryClientProvider, the BrowserRouter and the
 * top-level <App /> shell, plus a global ErrorBoundary so a render-time
 * exception is visible in the page itself instead of leaving a blank screen.
 */
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { App } from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";
import "./styles/theme.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

window.addEventListener("error", (event) => {
  // eslint-disable-next-line no-console
  console.error("[ANC global error]", event.error ?? event.message);
});
window.addEventListener("unhandledrejection", (event) => {
  // eslint-disable-next-line no-console
  console.error("[ANC unhandledrejection]", event.reason);
});

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error('No element with id="root" found in index.html');
}

// Apply persisted theme preference (default to dark) before React hydrates
// so there is no flash of light theme on slow networks.
const persistedTheme =
  (typeof window !== "undefined" &&
    window.localStorage.getItem("anc.theme")) ||
  "dark";
document.documentElement.setAttribute("data-theme", persistedTheme);

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Suspense
            fallback={
              <div className="status-block status-block--info">Cargando…</div>
            }
          >
            <App />
          </Suspense>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>,
);
